import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDatabase, faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import InternalShell, { useInternalApi } from '../layouts/InternalShell';
import { SUPER_ADMIN_NAV } from './nav';

interface DataOpsData {
    unapplied_migrations: string[]; unapplied_migrations_count: number; applied_migrations_count: number;
    table_counts: Record<string, number>;
}

const TABLE_LABELS: Record<string, string> = {
    patients: 'Patients', staff: 'Staff', institutions: 'Institutions', clinics: 'Clinics',
    individual_providers: 'Individual Providers', internal_users: 'Internal Users',
    dashboard_grants: 'Dashboard Grants (total)', active_dashboard_grants: 'Dashboard Grants (active)',
};

const DataOps: React.FC = () => {
    const { api } = useInternalApi();
    const [data, setData] = useState<DataOpsData | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => { document.title = 'Data Ops - Uzima Internal'; }, []);

    useEffect(() => {
        api.get('platform/admin/data-ops/').then(res => {
            if (res.data?.status === 'success') setData(res.data.data);
        }).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <InternalShell activeDashboard="super_admin" subNav={SUPER_ADMIN_NAV}>
            <h2 className="fw-bold mb-1"><FontAwesomeIcon icon={faDatabase} className="me-2 text-primary" />Data Ops</h2>
            <p className="text-muted mb-4">Migration status and core table row counts, read live from the database</p>

            {loading || !data ? (
                <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
                <>
                    <div className="row mb-4">
                        <div className="col-md-4 mb-3">
                            <div className={`card border-0 shadow-sm ${data.unapplied_migrations_count > 0 ? 'border-warning' : ''}`}>
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Unapplied Migrations</div>
                                    <h4 className={data.unapplied_migrations_count > 0 ? 'text-warning' : 'text-success'}>
                                        <FontAwesomeIcon icon={data.unapplied_migrations_count > 0 ? faExclamationTriangle : faCheckCircle} className="me-2" />
                                        {data.unapplied_migrations_count}
                                    </h4>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-4 mb-3">
                            <div className="card border-0 shadow-sm">
                                <div className="card-body">
                                    <div className="text-muted small mb-1">Applied Migrations</div>
                                    <h4>{data.applied_migrations_count}</h4>
                                </div>
                            </div>
                        </div>
                    </div>

                    {data.unapplied_migrations_count > 0 && (
                        <div className="alert alert-warning mb-4">
                            <strong>Unapplied:</strong> {data.unapplied_migrations.join(', ')}
                        </div>
                    )}

                    <div className="card border-0 shadow-sm mb-4">
                        <div className="card-header bg-white"><strong>Core Table Row Counts</strong></div>
                        <div className="card-body p-0">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light"><tr><th>Table</th><th>Rows</th></tr></thead>
                                <tbody>
                                    {Object.entries(data.table_counts).map(([key, count]) => (
                                        <tr key={key}><td>{TABLE_LABELS[key] || key}</td><td><span className="badge bg-secondary">{count.toLocaleString()}</span></td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="alert alert-secondary small mb-0">
                        Backup/disaster-recovery status and automated integrity checks are not yet built (tracker Phase 10.1, marked red at the start of this phase) — this page covers migration and row-count visibility only.
                    </div>
                </>
            )}
        </InternalShell>
    );
};

export default DataOps;
